import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
    Auth,
    AuthError,
    authState,
    browserLocalPersistence,
    browserSessionPersistence,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    updateProfile as firebaseUpdateProfile,
    getIdTokenResult,
    IdTokenResult,
    OAuthProvider,
    setPersistence,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
    User,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, EMPTY, from, map, Observable, of, Subscription, switchMap, take } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
import { isEmbeddedInIframe, isLocalEnvironment } from '../shared/utils/app-access.util';

/**
 * Response from createFirebaseCustomToken Cloud Function.
 */
interface CreateFirebaseTokenResponse {
    readonly firebaseToken: string;
    readonly email?: string;
}

/**
 * Token message received from StaffInterface via postMessage (legacy compatibility).
 * New integrations should use cookies (sso_ms_token / sso_firebase_token) instead.
 */
interface ParentTokenMessage {
    readonly type: 'MICROSOFT_TOKEN_RESPONSE';
    readonly idToken?: string;
    readonly customToken?: string;
    readonly error?: string;
}

/**
 * Error response from createFirebaseCustomToken Cloud Function.
 */
interface SsoErrorResponse {
    readonly error: string;
    readonly code?: 'NO_FIREBASE_USER' | 'NO_ROLE_ASSIGNED' | 'TOKEN_INVALID' | 'TOKEN_EXPIRED';
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    private static readonly AUTH_DEBUG_LOGS_KEY = 'auth-debug-logs';
    private static readonly MAX_DEBUG_LOG_ENTRIES = 300;
    /**
     * Cookie names that StaffInterface must set on the shared domain before embedding this app.
     *   sso_firebase_token — Firebase custom token (direct sign-in, no Cloud Function needed)
     *   sso_ms_token       — Microsoft ID token (exchanged via Cloud Function for a Firebase token)
     * Both cookies should be short-lived (≤5 min) and will be cleared after a single use.
     */
    private static readonly COOKIE_MS_TOKEN = 'sso_ms_token';
    private static readonly COOKIE_FIREBASE_TOKEN = 'sso_firebase_token';

    /**
     * @deprecated No longer used. With postMessage SSO, password login is not toggled dynamically.
     * Always returns false. Kept for backward compatibility with login.component.ts.
     */
    private readonly showPasswordLoginSubject = new BehaviorSubject<boolean>(false);
    private readonly microsoftLoginLoadingSubject = new BehaviorSubject<boolean>(false);
    private readonly loginNoticeSubject = new BehaviorSubject<string>('');
    /**
     * @deprecated No longer used. Microsoft account linking flow was removed with postMessage SSO.
     * Always returns false. Kept for backward compatibility with login.component.ts.
     */
    private readonly pendingMicrosoftLinkRequiredSubject = new BehaviorSubject<boolean>(false);

    private readonly authState$: Observable<User | null>;
    private readonly messageHandler: (event: MessageEvent) => void;
    private readonly authStateSubscription: Subscription;

    constructor(
        private readonly router: Router,
        private readonly auth: Auth,
        private readonly ngZone: NgZone
    ) {
        this.authState$ = authState(this.auth);
        this.messageHandler = this.handleParentMessage.bind(this);

        // Debug logging subscription (single source of truth for auth state)
        this.authStateSubscription = this.authState$.subscribe((user) => {
            this.debugLog('authState:changed', {
                hasUser: !!user,
                uid: user?.uid ?? null,
                email: user?.email ?? null,
            });
        });

        // Primary: auto-login from cookie (when StaffInterface sets cookie before iframe loads).
        // Fallback: postMessage listener for backward compatibility with StaffInterface.
        this.tryAuthFromCookie();
        this.setupParentMessageListener();
        this.debugLog('constructor:initialized');
    }

    public ngOnDestroy(): void {
        this.authStateSubscription.unsubscribe();
        window.removeEventListener('message', this.messageHandler);
        this.debugLog('ngOnDestroy:cleanup');
    }
    /**
     * @deprecated No longer needed with postMessage SSO flow. Returns immediately.
     * Kept for backward compatibility with login.component.ts
     */
    public checkMicrosoftRedirectResult(): Observable<void> {
        this.debugLog('checkMicrosoftRedirectResult:noop (SSO via postMessage)');
        return of(void 0);
    }

    /**
     * Initiates Microsoft SSO login by requesting a token from the parent window (StaffInterface).
     * TODO: Consider returning Observable<User> and letting component handle navigation (SRP)
     */
    public loginWithMicrosoft(): Observable<void> {
        this.debugLog('loginWithMicrosoft:start', { isInIframe: isEmbeddedInIframe() });
        this.loginNoticeSubject.next('');
        this.showPasswordLoginSubject.next(false);
        this.microsoftLoginLoadingSubject.next(true);

        // Use synchronous currentUser check to preserve the user-gesture context
        // required by browsers to allow signInWithPopup without being blocked.
        const currentUser = this.auth.currentUser;
        if (currentUser) {
            this.debugLog('loginWithMicrosoft:already-authenticated', {
                uid: currentUser.uid,
                email: currentUser.email ?? null,
            });
            this.microsoftLoginLoadingSubject.next(false);
            return from(this.router.navigate(['/dashboard'])).pipe(map(() => void 0));
        }

        // Check cookie set by StaffInterface first; fall back to direct popup (standalone).
        this.loginWithCookieOrDirect();
        return of(void 0);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // PUBLIC API: Email/Password Login
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Authenticates user with email and password.
     * TODO: Consider returning Observable<UserCredential> and letting component handle navigation (SRP)
     */
    public login(email: string, password: string, rememberMe: boolean): Observable<void> {
        const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
        this.debugLog('login:start', { email, persistence: rememberMe ? 'local' : 'session' });
        return from(setPersistence(this.auth, persistenceType)).pipe(
            switchMap(() => from(signInWithEmailAndPassword(this.auth, email, password))),
            switchMap(() => {
                this.debugLog('login:success');
                this.showPasswordLoginSubject.next(false);
                this.pendingMicrosoftLinkRequiredSubject.next(false);
                return from(this.router.navigate(['/dashboard']));
            }),
            map(() => void 0),
            catchError((error: unknown) => {
                this.debugError('login:error', error);
                const errorMessage = error instanceof Error ? error.message : 'Login failed';
                this.showNotification(`Login failed: ${errorMessage}`);
                return EMPTY;
            })
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // PUBLIC API: Password Reset
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Sends a password reset email.
     * @deprecated Use sendPasswordResetEmail() instead
     */
    public sendPasswordResetForPendingLink(email: string): Observable<void> {
        return this.sendPasswordResetEmail(email);
    }

    /**
     * Sends a password reset email.
     */
    public sendPasswordResetEmail(email: string): Observable<void> {
        const normalizedEmail = email.trim();
        if (!normalizedEmail) {
            this.loginNoticeSubject.next('Please enter your email address.');
            return of(void 0);
        }

        this.debugLog('sendPasswordResetEmail:start', { email: normalizedEmail });
        return from(firebaseSendPasswordResetEmail(this.auth, normalizedEmail)).pipe(
            map(() => {
                this.debugLog('sendPasswordResetEmail:success');
                this.loginNoticeSubject.next('Password reset email sent. Check your inbox.');
            }),
            catchError((error: unknown) => {
                this.debugError('sendPasswordResetEmail:error', error);
                const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
                this.showNotification(errorMessage);
                return EMPTY;
            })
        );
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // PUBLIC API: Logout
    // ─────────────────────────────────────────────────────────────────────────────

    /**
     * Signs out the current user and redirects to login.
     * TODO: Consider returning Promise<void> without navigation and letting component handle it (SRP)
     */
    public async logout(): Promise<void> {
        this.debugLog('logout:start');
        this.clearDebugLogs();
        this.showPasswordLoginSubject.next(false);
        this.pendingMicrosoftLinkRequiredSubject.next(false);
        this.loginNoticeSubject.next('');
        sessionStorage.setItem('explicit_logout', 'true');
        await signOut(this.auth);
        await this.router.navigate(['/login']);
    }

    // ─────────────────────────────────────────────────────────────────────────────
    // PUBLIC API: Observables (kept for backward compatibility)
    // ─────────────────────────────────────────────────────────────────────────────

    public isLoggedIn$(): Observable<boolean> {
        return this.authState$.pipe(map((user) => !!user));
    }

    /**
     * @deprecated Always returns false. Password login toggle was part of old Microsoft OAuth flow.
     * With postMessage SSO, only Microsoft login is shown. Remove usage in login.component.ts.
     */
    public showPasswordLogin$(): Observable<boolean> {
        return this.showPasswordLoginSubject.asObservable();
    }

    public microsoftLoginLoading$(): Observable<boolean> {
        return this.microsoftLoginLoadingSubject.asObservable();
    }

    public loginNotice$(): Observable<string> {
        return this.loginNoticeSubject.asObservable();
    }

    /**
     * @deprecated Always returns false. Microsoft account linking was part of old OAuth flow.
     * With postMessage SSO, linking is not required. Remove usage in login.component.ts.
     */
    public pendingMicrosoftLinkRequired$(): Observable<boolean> {
        return this.pendingMicrosoftLinkRequiredSubject.asObservable();
    }

    /**
     * Returns an observable that emits whether the user is authenticated.
     * Note: Consumer should handle subscription lifecycle (take(1), async pipe, etc.)
     */
    public hasAuthenticatedFirebaseSession$(): Observable<boolean> {
        return this.authState$.pipe(map((user) => !!user));
    }

    public getAuthenticatedUserEmail(): Observable<string | null> {
        return this.authState$.pipe(map((user) => user?.email ?? null));
    }

    public getAuthenticatedUserUid(): Observable<string | null> {
        return this.authState$.pipe(map((user) => user?.uid ?? null));
    }

    public getClaimsAuthenticatedUser(): Observable<IdTokenResult['claims'] | null> {
        return this.authState$.pipe(
            take(1),
            switchMap((user) => {
                if (!user) return of(null);
                return from(getIdTokenResult(user)).pipe(map((result) => result.claims));
            })
        );
    }

    public getAuthenticatedUserRole(): Observable<string | null> {
        return this.getClaimsAuthenticatedUser().pipe(
            map((claims) => {
                const role = claims?.['role'];
                return typeof role === 'string' ? role : null;
            })
        );
    }

    public async updateProfile(displayName: string): Promise<void> {
        const user = this.auth.currentUser;
        if (user) {
            await firebaseUpdateProfile(user, { displayName });
            this.showSuccessNotification('Profile updated successfully');
        }
    }

    /**
     * Silently checks for auth cookies on service init (auto-login for embedded iframe scenario).
     * Skips if the user is already authenticated.
     */
    private tryAuthFromCookie(): void {
        if (this.auth.currentUser) {
            this.debugLog('tryAuthFromCookie:skipped (already authenticated)');
            return;
        }

        const firebaseToken = this.readCookie(AuthService.COOKIE_FIREBASE_TOKEN);
        const msToken = this.readCookie(AuthService.COOKIE_MS_TOKEN);

        if (firebaseToken) {
            this.debugLog('tryAuthFromCookie:found sso_firebase_token');
            this.clearCookie(AuthService.COOKIE_FIREBASE_TOKEN);
            this.microsoftLoginLoadingSubject.next(true);
            this.signInWithFirebaseToken(firebaseToken);
        } else if (msToken) {
            this.debugLog('tryAuthFromCookie:found sso_ms_token');
            this.clearCookie(AuthService.COOKIE_MS_TOKEN);
            this.microsoftLoginLoadingSubject.next(true);
            this.exchangeMicrosoftTokenForFirebaseToken(msToken);
        } else {
            this.debugLog('tryAuthFromCookie:no cookie found');
        }
    }

    /**
     * Called when the user explicitly clicks "Sign in with Microsoft".
     * Re-checks cookies in case StaffInterface set them after the page loaded;
     * falls back to a direct Microsoft OAuth popup for standalone use.
     */
    private loginWithCookieOrDirect(): void {
        const firebaseToken = this.readCookie(AuthService.COOKIE_FIREBASE_TOKEN);
        const msToken = this.readCookie(AuthService.COOKIE_MS_TOKEN);

        if (firebaseToken) {
            this.debugLog('loginWithCookieOrDirect:sso_firebase_token from cookie');
            this.clearCookie(AuthService.COOKIE_FIREBASE_TOKEN);
            this.signInWithFirebaseToken(firebaseToken);
        } else if (msToken) {
            this.debugLog('loginWithCookieOrDirect:sso_ms_token from cookie');
            this.clearCookie(AuthService.COOKIE_MS_TOKEN);
            this.exchangeMicrosoftTokenForFirebaseToken(msToken);
        } else if (isEmbeddedInIframe()) {
            // In iframe (StaffInterface): no cookie set yet.
            // Request the token from StaffInterface via postMessage — it will respond
            // with MICROSOFT_TOKEN_RESPONSE and handleParentMessage() will complete login.
            // Once StaffInterface sets the sso_ms_token cookie before iframe load,
            // this branch will never be reached (cookie path above will handle it).
            this.debugLog('loginWithCookieOrDirect:in iframe, sending REQUEST_MICROSOFT_TOKEN to parent');
            window.parent.postMessage({ type: 'REQUEST_MICROSOFT_TOKEN', source: 'builder-admin-app' }, '*');
        } else if (isLocalEnvironment()) {
            // Standalone on localhost: no cookie → open Microsoft popup directly (local testing only).
            this.debugLog('loginWithCookieOrDirect:standalone-local, using popup');
            this.loginWithMicrosoftDirect();
        } else {
            // Outside localhost, direct access without StaffInterface's iframe is not allowed.
            this.debugLog('loginWithCookieOrDirect:blocked, not embedded in StaffInterface');
            this.microsoftLoginLoadingSubject.next(false);
            this.loginNoticeSubject.next('This application can only be accessed through StaffInterface.');
        }
    }

    private readCookie(name: string): string | null {
        const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
        return match ? decodeURIComponent(match[1]) : null;
    }

    private clearCookie(name: string): void {
        document.cookie = `${name}=; Max-Age=0; path=/`;
    }

    /**
     * Backward-compatibility listener for StaffInterface postMessage flow.
     * StaffInterface sends MICROSOFT_TOKEN_RESPONSE when the user is authenticated.
     * Remove once StaffInterface is updated to set cookies instead.
     */
    private setupParentMessageListener(): void {
        window.addEventListener('message', this.messageHandler);
        this.debugLog('setupParentMessageListener:registered (legacy compat)');
    }

    private handleParentMessage(event: MessageEvent): void {
        if (!event.data || typeof event.data !== 'object') {
            return;
        }

        const message = event.data as Partial<ParentTokenMessage>;
        if (message.type !== 'MICROSOFT_TOKEN_RESPONSE') {
            return;
        }

        this.debugLog('handleParentMessage:received', {
            hasIdToken: !!message.idToken,
            hasCustomToken: !!message.customToken,
            hasError: !!message.error,
        });

        this.ngZone.run(() => {
            if (message.error) {
                this.microsoftLoginLoadingSubject.next(false);
                this.loginNoticeSubject.next(message.error);
                return;
            }

            if (message.customToken) {
                this.microsoftLoginLoadingSubject.next(true);
                this.signInWithFirebaseToken(message.customToken);
                return;
            }

            if (message.idToken) {
                this.microsoftLoginLoadingSubject.next(true);
                this.exchangeMicrosoftTokenForFirebaseToken(message.idToken);
                return;
            }

            this.microsoftLoginLoadingSubject.next(false);
            this.loginNoticeSubject.next('No token received from parent. Please try again.');
        });
    }

    private loginWithMicrosoftDirect(): void {
        this.debugLog('loginWithMicrosoftDirect:start');

        const provider = new OAuthProvider('microsoft.com');
        provider.setCustomParameters({ tenant: environment.microsoftTenantId });

        from(signInWithPopup(this.auth, provider))
            .pipe(
                map((result) => {
                    const credential = OAuthProvider.credentialFromResult(result);
                    if (!credential?.idToken) {
                        throw new Error('No Microsoft ID token received. Please try again.');
                    }
                    return { idToken: credential.idToken, needsSignOut: true };
                }),
                catchError((error: unknown) => {
                    // Firebase throws this error when the email already exists under a
                    // different credential (e.g. a previous custom-token sign-in).
                    // The Microsoft idToken is still present in the error object.
                    if (this.isAccountExistsError(error)) {
                        const credential = OAuthProvider.credentialFromError(error as AuthError);
                        if (credential?.idToken) {
                            this.debugLog('loginWithMicrosoftDirect:account-conflict-resolved');
                            return of({ idToken: credential.idToken, needsSignOut: false });
                        }
                    }
                    throw error;
                }),
                switchMap(({ idToken, needsSignOut }) => {
                    if (needsSignOut) {
                        this.debugLog('loginWithMicrosoftDirect:signing-out-temporary-session');
                        return from(signOut(this.auth)).pipe(map(() => idToken));
                    }
                    return of(idToken);
                }),
                switchMap((idToken) => this.buildTokenExchangePipeline(idToken)),
                catchError((error: unknown) => {
                    this.debugError('loginWithMicrosoftDirect:error', error);
                    this.microsoftLoginLoadingSubject.next(false);
                    const errorMessage = error instanceof Error ? error.message : 'Microsoft login failed';
                    this.showNotification(errorMessage);
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private isAccountExistsError(error: unknown): boolean {
        return (
            typeof error === 'object' &&
            error !== null &&
            'code' in error &&
            (error as { code: string }).code === 'auth/account-exists-with-different-credential'
        );
    }

    /**
     * Exchanges Microsoft idToken for Firebase custom token via Cloud Function.
     */
    private exchangeMicrosoftTokenForFirebaseToken(msIdToken: string): void {
        this.debugLog('exchangeMicrosoftToken:start');
        this.buildTokenExchangePipeline(msIdToken)
            .pipe(
                catchError((error: unknown) => {
                    this.debugError('exchangeMicrosoftToken:error', error);
                    this.microsoftLoginLoadingSubject.next(false);
                    const errorMessage = error instanceof Error ? error.message : 'Microsoft login failed';
                    this.showNotification(errorMessage);
                    return EMPTY;
                })
            )
            .subscribe();
    }

    private buildTokenExchangePipeline(msIdToken: string): Observable<boolean> {
        const functionUrl = this.getCloudFunctionUrl();
        return from(
            fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: msIdToken }),
            })
        ).pipe(
            switchMap((response) => {
                if (!response.ok) {
                    const status = response.status;
                    return from(response.json()).pipe(
                        switchMap((errorData: SsoErrorResponse) => {
                            const userMessage = this.mapErrorToUserMessage(status, errorData);
                            throw new Error(userMessage);
                        })
                    );
                }
                return from(response.json() as Promise<CreateFirebaseTokenResponse>);
            }),
            switchMap((data) => {
                if (!data.firebaseToken) {
                    throw new Error('No Firebase token received from server');
                }
                this.debugLog('buildTokenExchangePipeline:received-token', { email: data.email });
                return from(signInWithCustomToken(this.auth, data.firebaseToken));
            }),
            switchMap((result) => {
                this.debugLog('buildTokenExchangePipeline:success', {
                    uid: result.user?.uid,
                    email: result.user?.email,
                });
                this.microsoftLoginLoadingSubject.next(false);
                return from(this.router.navigate(['/dashboard']));
            })
        );
    }

    /**
     * Maps HTTP status codes and error codes to user-friendly messages.
     */
    private mapErrorToUserMessage(status: number, errorData: SsoErrorResponse): string {
        if (status === 403) {
            if (errorData.code === 'NO_FIREBASE_USER') {
                return 'Your account is not provisioned for this application. Contact your administrator.';
            }
            if (errorData.code === 'NO_ROLE_ASSIGNED') {
                return 'Your account exists but has no assigned role. Contact your administrator.';
            }
            return 'You are not authorized to access this application.';
        }
        if (status === 401) {
            return 'Your Microsoft session has expired. Please try again.';
        }
        return errorData.error || 'Login failed. Please try again or contact support.';
    }

    /**
     * Signs in to Firebase using a custom token (when parent sends it directly).
     */
    private signInWithFirebaseToken(firebaseToken: string): void {
        this.debugLog('signInWithFirebaseToken:start');

        from(signInWithCustomToken(this.auth, firebaseToken))
            .pipe(
                switchMap((result) => {
                    this.debugLog('signInWithFirebaseToken:success', {
                        uid: result.user?.uid,
                        email: result.user?.email,
                    });
                    this.microsoftLoginLoadingSubject.next(false);
                    return from(this.router.navigate(['/dashboard']));
                }),
                catchError((error: unknown) => {
                    this.debugError('signInWithFirebaseToken:error', error);
                    this.microsoftLoginLoadingSubject.next(false);
                    const errorMessage = error instanceof Error ? error.message : 'Microsoft login failed';
                    this.showNotification(errorMessage);
                    return EMPTY;
                })
            )
            .subscribe();
    }
    private getCloudFunctionUrl(): string {
        if (environment.production) {
            return 'https://createfirebasecustomtoken-xztp6e2jpq-uc.a.run.app'; // producción
        }
        return 'https://createfirebasecustomtoken-xli6zxmfza-uc.a.run.app'; // staging
    }

    private showNotification(message: string): void {
        Swal.fire({
            icon: 'error',
            text: message,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Close',
            allowOutsideClick: true,
        });
    }

    private showSuccessNotification(message: string): void {
        Swal.fire({
            icon: 'success',
            text: message,
            showConfirmButton: false,
            timer: 2000,
            allowOutsideClick: true,
        });
    }

    private debugLog(message: string, data?: unknown): void {
        if (data !== undefined) {
            console.log(`[AuthService] ${message}`, data);
        } else {
            console.log(`[AuthService] ${message}`);
        }
        this.persistDebugLog('log', message, data);
    }

    private debugError(message: string, data?: unknown): void {
        if (data !== undefined) {
            console.error(`[AuthService] ${message}`, data);
        } else {
            console.error(`[AuthService] ${message}`);
        }
        this.persistDebugLog('error', message, data);
    }

    private persistDebugLog(level: 'log' | 'error', message: string, data?: unknown): void {
        try {
            const rawLogs = sessionStorage.getItem(AuthService.AUTH_DEBUG_LOGS_KEY);
            const parsedLogs = rawLogs ? (JSON.parse(rawLogs) as string[]) : [];
            const payload = data === undefined ? '' : ` | ${JSON.stringify(data)}`;
            const entry = `${new Date().toISOString()} | ${level.toUpperCase()} | ${message}${payload}`;
            parsedLogs.push(entry);

            if (parsedLogs.length > AuthService.MAX_DEBUG_LOG_ENTRIES) {
                parsedLogs.splice(0, parsedLogs.length - AuthService.MAX_DEBUG_LOG_ENTRIES);
            }

            sessionStorage.setItem(AuthService.AUTH_DEBUG_LOGS_KEY, JSON.stringify(parsedLogs));
        } catch {
            // Ignore storage errors
        }
    }

    private clearDebugLogs(): void {
        try {
            sessionStorage.removeItem(AuthService.AUTH_DEBUG_LOGS_KEY);
        } catch {
            // Ignore storage errors
        }
    }
}
